import { Component, inject, Inject } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IContactGroupDeleteData, IContactWidget } from '../../interfaces/contact-widget.interface';
import { ContactGroup } from '../../interfaces/contact.interface';
import { ContactWidgetService } from '../../services/contact-widget.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'lib-contact-group-delete',
	imports: [
		SharedModule
	],
  templateUrl: './contact-group-delete.component.html',
  styleUrl: './contact-group-delete.component.scss'
})
export class ContactGroupDeleteComponent {
	// Services
	private readonly contactWidgetService: ContactWidgetService = inject(ContactWidgetService);
	private readonly snackBar: MatSnackBar = inject(MatSnackBar);
	private readonly translate: TranslateService = inject(TranslateService);

	contactWidget: IContactWidget | undefined;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IContactGroupDeleteData,
		private readonly dialogRef: MatDialogRef<ContactGroupDeleteComponent>,
	) {
		this.contactWidget = data?.widget;
	}

	protected async confirm(): Promise<void> {
		if (!this.contactWidget?._id) {
			console.error('[ContactGroupDelete] Cannot delete group - widget ID missing');
			this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_DELETE_GROUP'),
				'Close',
				{ duration: 3000 }
			);
			return;
		}

		try {
			// Delete the group
			await this.deleteContactGroup(this.data.groupId);

			// Save the widget settings to database
			await this.contactWidgetService.saveContactWidgetSettingsToDatabase(
				this.contactWidget._id,
				this.contactWidget
			);

			this.snackBar.open(
				`${this.translate.instant('ASTIMAX.CONTACT.WIDGET.GROUP')} "${this.data.name}" ${this.translate.instant('ASTIMAX.CONTACT.WIDGET.DELETE_SUCCESSFULLY')}`,
				'Close',
				{ duration: 3000 }
			);

			// Close dialog with success result
			this.dialogRef.close({
				widget: this.contactWidget,
				groupId: this.data.groupId,
				name: this.data.name,
				success: true
			});

		} catch (error) {
			console.error('[ContactGroupDelete] Error deleting group:', error);
			this.snackBar.open(this.translate.instant('ASTIMAX.CONTACT.WIDGET.FAILED_TO_DELETE_GROUP'),
				'Close',
				{ duration: 3000 }
			);
		}
	}

	private async deleteContactGroup(groupId: number): Promise<void> {
		if (!this.contactWidget?._id) {
			throw new Error('Widget ID missing');
		}

		// Get current groups from service
		const widgetData = this.contactWidgetService.getWidgetData(this.contactWidget._id);
		if (!widgetData) {
			throw new Error('Widget data not found');
		}

		const currentGroups = widgetData.contactGroups;
		const groupToDelete = currentGroups.find((g: ContactGroup) => g.groupId === groupId);
		if (!groupToDelete) {
			throw new Error('Group not found');
		}

		// Remove the group
		const updatedGroups = currentGroups.filter((g: ContactGroup) => g.groupId !== groupId);

		// Update contact groups through service
		this.contactWidgetService.updateContactGroups(this.contactWidget._id, updatedGroups);

		// Update widget settings
		if (this.contactWidget.settings) {
			this.contactWidget.settings.contactGroups = updatedGroups;
		}
	}
}
